import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Button,
  useToast,
  Checkbox,
  Input,
  Flex,
  Alert,
  AlertIcon,
  Tooltip,
  TableContainer,
  Heading,
  Image,
} from "@chakra-ui/react";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useAppContext } from "../../../../contexts/AppContext";
import excelMockup from "../../../../assets/img/excelMockup.png"
import CloseModalArrow from "../../../general/CloseModalArrow";

const NavUploadDrawer = ({ isOpen, onClose, nav, onValidated }) => {
  const { formatDate } = useAppContext();
  const [consistencyIssues, setConsistencyIssues] = useState([]);
  const [comparisonState, setComparisonState] = useState({});
  const toast = useToast();
  const [checkAll, setCheckAll] = useState(false);
  const handleCheckAll = () => {
    const updated = {};
    Object.entries(comparisonState).forEach(([section, values]) => {
      updated[section] = {
        ...values,
        checked: !checkAll,
      };
    });
    setComparisonState(updated);
    runConsistencyChecks(updated);
    setCheckAll(!checkAll);
  };
  useEffect(() => {
    const allChecked = Object.values(comparisonState).every((v) => v.checked);
    setCheckAll(allChecked);
  }, [comparisonState]);

  useEffect(() => {
    if (isOpen && nav?.navComparison) {
      const firstSection = Object.values(nav.navComparison)[0] || {};
      const latest = Object.keys(firstSection).find((k) => k.includes("/"));
      const previous = Object.keys(firstSection).find((k) => k !== latest && k.includes("/"));
  
      const clone = JSON.parse(JSON.stringify(nav.navComparison));
      const entries = Object.entries(clone);
      const total = entries.length;
  
      // Si c’est un nouveau fichier, on injecte des incohérences
      if (nav.isNewUpload) {
        const numberOfInconsistencies = Math.floor(total * 0.2);
        const inconsistentKeys = new Set();
        while (inconsistentKeys.size < numberOfInconsistencies) {
          const randomIndex = Math.floor(Math.random() * total);
          inconsistentKeys.add(entries[randomIndex][0]);
        }
  
        entries.forEach(([key, value]) => {
          const latestValue = value[latest];
          const previousValue = value[previous];
  
          if (typeof latestValue === "number" && typeof previousValue === "number") {
            const shouldBeInconsistent = inconsistentKeys.has(key);
  
            const variationFactor = shouldBeInconsistent
              ? 1 + Math.random() * 0.3 + 0.2 // 20% à 50%
              : 1 + Math.random() * 0.1;     // 0% à 10%
  
            const simulated = previousValue * variationFactor;
            value[latest] = Math.round(simulated);
            value.change = Math.round(simulated - previousValue);
            value.changePercent = parseFloat(
              ((simulated - previousValue) / previousValue).toFixed(4)
            );
          } else {
            value.change = null;
            value.changePercent = null;
          }
  
          value.checked = false;
        });
      } else {
        // Cas d'ouverture d'un nav déjà existant : juste reset les checked
        entries.forEach(([_, value]) => {
          value.checked = false;
        });
      }
  
      const updated = Object.fromEntries(entries);
      setComparisonState(updated);
      runConsistencyChecks(updated);
    }
  }, [isOpen, nav]);
  
  
  

  const runConsistencyChecks = (data) => {
    const issues = [];
    Object.entries(data).forEach(([section, values]) => {
      const variation = parseFloat(values.changePercent || 0);
      if (variation > 0.2 || variation < -0.2) {
        if (!values.checked) {
          issues.push(section);
        }
      }
    });
    setConsistencyIssues(issues);
  };

  const handleValidate = () => {
    toast({
      title: "NAV validated",
      description: `NAV for ${nav.valuationDate} has been marked as validated`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  
    if (nav?.isNewUpload && onValidated) {
      onValidated({ ...nav, status: "validated" });
    }
  
    onClose();
  };
  

  const handleChange = (section, field, value) => {
    setComparisonState((prev) => {
      const updated = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
      runConsistencyChecks(updated);
      return updated;
    });
  };

  const handleCheckbox = (section) => {
    setComparisonState((prev) => {
      const updated = {
        ...prev,
        [section]: {
          ...prev[section],
          checked: !prev[section].checked,
        },
      };
      runConsistencyChecks(updated);
      return updated;
    });
  };

  const comparison = comparisonState;
  const firstSection = Object.values(comparison)[0] || {};
  const latestDate = Object.keys(firstSection).find((k) => k.includes("/"));
  const previousDate = Object.keys(firstSection).find(
    (k) => k !== latestDate && k.includes("/")
  );

  const isValidatable = Object.values(comparison).every((v) => v.checked);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader
          gap={4}
          alignItems="center"
          borderBottom={"1px solid"}
          borderBottomColor={"grey.200"}>
         <CloseModalArrow onClose={onClose} />
          Review NAV Upload
        </DrawerHeader>
        <DrawerBody overflow={"hidden"} p={0}>
          <Flex alignItems="flex-start" h="100%">
            {/* Left: document preview */}
            <Box flex="1" borderRightWidth={1} h="100%">
              <Box
                bg="grey.50"
                h="100%"
                minH="400px"
                textAlign="center"
                color="gray.400">
                <Image src={excelMockup} objectFit={'cover'} h={"100%"} w='100%' objectPosition={'top left'} />
              </Box>
            </Box>

            {/* Right: NAV Table and checks */}
            <Flex align="start" direction="column" height={"100%"} flex={2}>
              <Flex
                alignItems={"center"}
                justifyContent={"space-between"}
                w={"full"}
                borderBottom={"1px solid"}
                borderBottomColor={"grey.200"}
                py={4}
                px={8}>
                <Flex gap={8} alignItems={"center"}>
                  <Box>
                    <Text>Uploaded document</Text>
                    <Text
                      color="grey.700"
                      fontWeight="bold"
                      maxWidth={"200px"}
                      textOverflow={"ellipsis"}
                      overflow={"hidden"}
                      noOfLines={1}>
                      {nav?.sourceFile}
                    </Text>
                  </Box>
                  <Box>
                    <Text>Fund</Text>
                    <Text color="grey.700" fontWeight="bold">
                      {nav?.fundName}
                    </Text>
                  </Box>
                  <Box>
                    <Text>Valuation date</Text>
                    <Text color="grey.700" fontWeight="bold">
                      {formatDate(nav?.valuationDate)}
                    </Text>
                  </Box>{" "}
                </Flex>
              </Flex>


              <Flex w="full" direction="column" gap={8} flexGrow={1} height={"calc(100% - 76px)"} pt={8} px={8}>
                
                  <Heading size={'md'}>NAV comparison</Heading>
            
                <Box overflowY={"auto"} h='100%' pb={24}  >
                  <TableContainer>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Section</Th>
                          <Th isNumeric>{latestDate}</Th>
                          <Th isNumeric>{previousDate}</Th>
                          <Th isNumeric>Change</Th>
                          <Th isNumeric>Change %</Th>
                          <Th>
                            <Checkbox
                              isChecked={checkAll}
                              onChange={handleCheckAll}
                              isIndeterminate={
                                Object.values(comparisonState).some(
                                  (v) => v.checked
                                ) && !checkAll
                              }
                            />
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Object.entries(comparison).map(([section, values]) => {
                          const variation = parseFloat(
                            values.changePercent || 0
                          );
                          const isInconsistent =
                            variation > 0.2 || variation < -0.2;
                          const highlight = isInconsistent && !values.checked;
                          return (
                            <Tr
                              key={section}
                              bg={highlight ? "orange.50" : "transparent"}>
                              <Td fontWeight={500}>
                                <Flex gap={2} alignItems={"center"}>
                                  {highlight ? (
                                    <Tooltip label="Variation > 20%">
                                      <span>
                                        <Icon
                                          as={AlertCircle}
                                          color="orange.600"
                                          w={"16px !important"}
                                          h={"16px !important"}
                                        />
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip label="No issues">
                                      <span>
                                        <Icon
                                          as={CheckCircle}
                                          color="green.500"
                                          w={"16px !important"}
                                          h={"16px !important"}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}

                                  <Tooltip label={section}>
                                    <Text
                                      fontWeight={"500"}
                                      color={"grey.900"}
                                      maxWidth={"15vw"}
                                      textOverflow={"ellipsis"}
                                      overflow={"hidden"}>
                                      {section}
                                    </Text>
                                  </Tooltip>
                                </Flex>
                              </Td>
                              <Td isNumeric>
                                <Input
                                  size="sm"
                                  textAlign="right"
                                  value={values[latestDate] || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      section,
                                      latestDate,
                                      e.target.value
                                    )
                                  }
                                />
                              </Td>
                              <Td isNumeric>
                                <Input
                                  size="sm"
                                  textAlign="right"
                                  value={values[previousDate] || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      section,
                                      previousDate,
                                      e.target.value
                                    )
                                  }
                                />
                              </Td>
                              <Td isNumeric>
                                <Input
                                  size="sm"
                                  textAlign="right"
                                  value={values.change || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      section,
                                      "change",
                                      e.target.value
                                    )
                                  }
                                />
                              </Td>
                              <Td isNumeric>
                                <Input
                                  size="sm"
                                  textAlign="right"
                                  value={values.changePercent || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      section,
                                      "changePercent",
                                      e.target.value
                                    )
                                  }
                                />
                              </Td>

                              <Td>
                                <Checkbox
                                  isChecked={values.checked}
                                  onChange={() => handleCheckbox(section)}
                                />
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Flex>

              <Flex
                px={8}
                py={4}
                alignItems={"center"}
                gap={4}
                position={"sticky"}
                bottom={0}
                w={"full"}
                justifyContent={"flex-end"}
                bg={
                  "linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,1))"
                }>
                <Box w={"fit-content"}>
                  {consistencyIssues.length === 0 ? (
                    <Alert status="success" borderRadius={8}>
                      <AlertIcon />
                      All consistency checks passed
                    </Alert>
                  ) : (
                    <Alert status="warning" borderRadius={8}>
                      <AlertIcon />
                      {consistencyIssues.length}
                      {consistencyIssues.length > 1
                        ? " inconsistencies"
                        : " inconsistency"}{" "}
                      found.
                    </Alert>
                  )}
                </Box>

                <Button onClick={onClose} variant={"outline"}>
                  Save
                </Button>

                <Tooltip
                  label={
                    !isValidatable
                      ? "Please check all rows and fix consistency issues"
                      : ""
                  }
                  shouldWrapChildren>
                  <Button
                    alignSelf="flex-end"
                    onClick={handleValidate}
                    isDisabled={!isValidatable}>
                    Validate NAV
                  </Button>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default NavUploadDrawer;
